import { toast } from '../../../../core'
import { useCurrentUser } from '../../../utils/currentUserContext'
import { useApiOrgSlug } from '../../../utils/orgSlug'
import {
  buildApiQueryKey,
  useApiQueryClient,
  useApiMutation,
} from '../../../utils/reactQuery'
import {
  EncounterBulkUpdateResultToastContent,
  EncounterBulkUpdateResultToastDismiss,
} from '../components/EncounterBulkUpdateResultToastContent'
import { EncounterUpdateViolationCodeSchema } from '../components/encounterUpdateViolationCodeSchema'
import type {
  UpdateEncounterMutationContext,
  UpdateEncounterMutationOptions,
  UpdateEncounterVariables,
} from './useEncounterMutations.types'
import type { operations, components } from '@joyful/openapi/types.gen'
import { extractErrorMessage } from '@joyful/utils'
import { useMutation } from '@tanstack/react-query'
import type { SuccessResponseJSON } from 'openapi-typescript-helpers'

interface UpdateEncounterDetailMutationContext {
  optimisticEncounter: components['schemas']['EncounterAPIResponse'] | undefined
  previousEncounter: components['schemas']['EncounterAPIResponse'] | undefined
  queryKey: UpdateEncounterMutationContext['queryKey']
}

const pendingEncounterDetailUpdates = new Map<string, number>()

function encounterDetailQueryKeyToken(
  queryKey: UpdateEncounterDetailMutationContext['queryKey'],
) {
  return JSON.stringify(queryKey)
}

function incrementPendingEncounterDetailUpdate(
  queryKey: UpdateEncounterDetailMutationContext['queryKey'],
) {
  const token = encounterDetailQueryKeyToken(queryKey)
  const nextCount = (pendingEncounterDetailUpdates.get(token) ?? 0) + 1
  pendingEncounterDetailUpdates.set(token, nextCount)
}

function decrementPendingEncounterDetailUpdate(
  queryKey: UpdateEncounterDetailMutationContext['queryKey'],
) {
  const token = encounterDetailQueryKeyToken(queryKey)
  const currentCount = pendingEncounterDetailUpdates.get(token)
  if (currentCount !== undefined) {
    pendingEncounterDetailUpdates.set(token, Math.max(0, currentCount - 1))
  }
}

export function useUpdateEncounterMutation(
  options?: UpdateEncounterMutationOptions,
) {
  const client = useApiQueryClient()

  const orgSlug = useApiOrgSlug()
  const { invalidateApiQueries, queryClient } = client

  const _updateEncounter = useApiMutation(
    'patch',
    '/{org_slug}/encounters/{encounter_id}',
  )
  return useMutation<
    SuccessResponseJSON<operations['updateEncounter']>,
    Error,
    UpdateEncounterVariables,
    UpdateEncounterDetailMutationContext
  >({
    ...options,
    mutationFn: async (variables) => {
      return _updateEncounter.mutateAsync({
        body: {
          assignee_biller_user_id:
            variables.assigneeBillerUser === null
              ? null
              : variables.assigneeBillerUser?.id,
          enrichment_status_override: variables.enrichment_status_override,
          enrichment_status_override_reason:
            variables.enrichment_status_override_reason,
          flagged_for_joyful_followup: variables.flagged_for_joyful_followup,
          snoozed_until: variables.snoozed_until,
          snoozed_until_is_indefinite: variables.snoozed_until_is_indefinite,
          workflow_status: variables.workflow_status,
        },
        params: {
          path: {
            encounter_id: variables.encounterId,
            org_slug: orgSlug,
          },
        },
      })
    },

    onError: (error, _newData, context) => {
      if (
        context?.optimisticEncounter &&
        queryClient.getQueryData(context.queryKey) ===
          context.optimisticEncounter
      ) {
        queryClient.setQueryData(context.queryKey, context.previousEncounter)
      }

      toast.error(`Failed to update encounter: ${extractErrorMessage(error)}`)
    },

    onMutate: async (variables) => {
      const queryKey = buildApiQueryKey(
        '/{org_slug}/encounters/{encounter_id}',
        {
          params: {
            path: {
              encounter_id: variables.encounterId,
              org_slug: orgSlug,
            },
          },
        },
      )

      incrementPendingEncounterDetailUpdate(queryKey)
      const onMutate = options?.onMutate

      try {
        if (onMutate !== undefined) {
          await onMutate(variables)
        }

        // cancel any outgoing refetches so they don't overwrite our optimistic update
        await queryClient.cancelQueries({
          queryKey,
        })

        // snapshot the previous value
        const previousEncounter =
          queryClient.getQueryData<
            SuccessResponseJSON<operations['getEncounter']>
          >(queryKey)

        if (previousEncounter === undefined) {
          return { optimisticEncounter: undefined, previousEncounter, queryKey }
        }

        const optimisticEncounter = queryClient.setQueryData<
          SuccessResponseJSON<operations['getEncounter']>
        >(
          queryKey,
          buildOptimisticEncounterUpdate({
            previousEncounter,
            variables,
          }),
        )

        return {
          optimisticEncounter,
          previousEncounter,
          queryKey,
        }
      } catch (error) {
        decrementPendingEncounterDetailUpdate(queryKey)
        throw error
      }
    },

    onSettled: async (_result, _error, variables, context) => {
      const queryKey =
        context?.queryKey ??
        buildApiQueryKey('/{org_slug}/encounters/{encounter_id}', {
          params: {
            path: { encounter_id: variables.encounterId, org_slug: orgSlug },
          },
        })
      if (context !== undefined) {
        decrementPendingEncounterDetailUpdate(queryKey)
      }

      await invalidateApiQueries(
        '/{org_slug}/encounters',
        '/{org_slug}/encounters/items',
        '/{org_slug}/projects/{project_id}/encounters',
        '/{org_slug}/work_blocks',
      )

      const token = encounterDetailQueryKeyToken(queryKey)
      if (pendingEncounterDetailUpdates.get(token) === 0) {
        pendingEncounterDetailUpdates.delete(token)
        const params = {
          path: {
            encounter_id: variables.encounterId,
            org_slug: orgSlug,
          },
        }

        await Promise.all([
          queryClient.invalidateQueries({ exact: true, queryKey }),
          invalidateApiQueries({
            options: { params },
            path: '/{org_slug}/encounters/{encounter_id}/timeline',
          }),
        ])
      }
    },

    onSuccess: async (result, variables, context) => {
      if (!result.success) {
        if (
          context?.optimisticEncounter &&
          queryClient.getQueryData(context.queryKey) ===
            context.optimisticEncounter
        ) {
          queryClient.setQueryData(context.queryKey, context.previousEncounter)
        }

        toast.error(
          [
            `Failed to update encounter`,
            result.violations?.map((violation) => {
              return EncounterUpdateViolationCodeSchema.meta(violation).title
            }),
          ]
            .filter(Boolean)
            .join(': '),
        )
        return
      }

      options?.onSuccess?.(result, variables)
    },
  })
}

export const OptimisticTagIdPrefix = '__optimistic__'

export function useAddTagToEncounterMutation() {
  const client = useApiQueryClient()

  const currentUser = useCurrentUser()
  const { invalidateApiQueries, queryClient } = client

  return useApiMutation('post', '/{org_slug}/encounters/{encounter_id}/tags', {
    onError: (error, _newData, _context) => {
      // SAFETY: react-query does not propagate onMutate context into onError.
      const context = _context as UpdateEncounterMutationContext | undefined

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context !== undefined) {
        queryClient.setQueryData(context.queryKey, context.previousEncounter)
      }

      toast.error(
        [`Failed to update encounter`, error.detail?.at(0)?.msg]
          .filter(Boolean)
          .join(': '),
      )
    },

    onMutate: async (variables) => {
      const queryKey = buildApiQueryKey(
        '/{org_slug}/encounters/{encounter_id}',
        {
          params: {
            path: {
              encounter_id: variables.params.path.encounter_id,
              org_slug: variables.params.path.org_slug,
            },
          },
        },
      )

      // cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey,
      })

      // snapshot the previous value
      const previousEncounter =
        queryClient.getQueryData<
          SuccessResponseJSON<operations['getEncounter']>
        >(queryKey)

      if (previousEncounter === undefined) {
        return undefined
      }

      const tagId = [OptimisticTagIdPrefix, crypto.randomUUID()].join('')
      const now = new Date().toISOString()
      queryClient.setQueryData<SuccessResponseJSON<operations['getEncounter']>>(
        queryKey,
        {
          ...previousEncounter,
          encounter_tags: previousEncounter.encounter_tags.concat({
            assigned_by_user_id: currentUser.id,
            created_at: now,
            encounter_id: previousEncounter.id,
            id: [OptimisticTagIdPrefix, crypto.randomUUID()].join(''),
            modified_at: now,
            tag: {
              account_id: crypto.randomUUID(),
              color: variables.body.color,
              created_at: now,
              created_by_user_id: currentUser.id,
              description: variables.body.description ?? null,
              id: tagId,
              modified_at: now,
              title: variables.body.title,
            },
            tag_id: tagId,
          }),
        },
      )

      return {
        previousEncounter,
        queryKey,
      }
    },

    onSettled: async () => {
      return invalidateApiQueries(
        '/{org_slug}/projects/{project_id}/encounters',
      )
    },

    onSuccess: async (_, variables) => {
      await invalidateApiQueries(
        '/{org_slug}/encounters',
        '/{org_slug}/encounters/items',
        '/{org_slug}/projects/{project_id}/encounters',
        {
          options: { params: { path: variables.params.path } },
          path: '/{org_slug}/encounters/{encounter_id}',
        },
        {
          options: { params: { path: variables.params.path } },
          path: '/{org_slug}/encounters/{encounter_id}/timeline',
        },
      )
    },
  })
}

export function useRemoveTagFromEncounterMutation() {
  const client = useApiQueryClient()

  const { invalidateApiQueries, queryClient } = client

  return useApiMutation(
    'delete',
    '/{org_slug}/encounters/{encounter_id}/tags/{encounter_tag_id}',
    {
      onError: (error, _newData, _context) => {
        // SAFETY: react-query does not propagate onMutate context into onError.
        const context = _context as UpdateEncounterMutationContext | undefined

        // If the mutation fails, use the context returned from onMutate to roll back
        if (context !== undefined) {
          queryClient.setQueryData(context.queryKey, context.previousEncounter)
        }

        toast.error(
          [`Failed to update encounter`, error.detail?.at(0)?.msg]
            .filter(Boolean)
            .join(': '),
        )
      },

      onMutate: async (variables) => {
        const queryKey = buildApiQueryKey(
          '/{org_slug}/encounters/{encounter_id}',
          {
            params: {
              path: {
                encounter_id: variables.params.path.encounter_id,
                org_slug: variables.params.path.org_slug,
              },
            },
          },
        )

        // cancel any outgoing refetches so they don't overwrite our optimistic update
        await queryClient.cancelQueries({
          queryKey,
        })

        // snapshot the previous value
        const previousEncounter =
          queryClient.getQueryData<
            SuccessResponseJSON<operations['getEncounter']>
          >(queryKey)

        if (previousEncounter === undefined) {
          return undefined
        }

        queryClient.setQueryData<
          SuccessResponseJSON<operations['getEncounter']>
        >(queryKey, {
          ...previousEncounter,
          encounter_tags: previousEncounter.encounter_tags.filter(
            (encounterTag) => {
              return encounterTag.id !== variables.params.path.encounter_tag_id
            },
          ),
        })

        return {
          previousEncounter,
          queryKey,
        }
      },

      onSettled: async () => {
        return invalidateApiQueries(
          '/{org_slug}/projects/{project_id}/encounters',
        )
      },

      onSuccess: async (_, variables) => {
        await invalidateApiQueries(
          '/{org_slug}/encounters',
          '/{org_slug}/encounters/items',
          '/{org_slug}/projects/{project_id}/encounters',
          {
            options: { params: { path: variables.params.path } },
            path: '/{org_slug}/encounters/{encounter_id}',
          },
          {
            options: { params: { path: variables.params.path } },
            path: '/{org_slug}/encounters/{encounter_id}/timeline',
          },
        )
      },
    },
  )
}

export type BulkUpdateEncountersVariables = Pick<
  UpdateEncounterVariables,
  'assigneeBillerUser'
> &
  Omit<
    components['schemas']['BulkUpdateEncountersRequest'],
    'assignee_biller_user_id'
  >

function buildLoadingToastMessage(variables: BulkUpdateEncountersVariables) {
  if (variables.assigneeBillerUser !== undefined) {
    return variables.assigneeBillerUser === null
      ? `Unassigning biller assignee for encounters…`
      : `Setting biller assignee for encounters…`
  }

  if (variables.flagged_for_joyful_followup !== undefined) {
    return variables.flagged_for_joyful_followup
      ? `Flagging encounters for Joyful Health follow-up…`
      : `Unflagging encounters for Joyful Health follow-up…`
  }

  if (variables.snoozed_until !== undefined) {
    return variables.snoozed_until === null
      ? `Unsnoozing encounters…`
      : `Snoozing encounters…`
  }

  return `Updating encounters…`
}

export function useBulkUpdateEncountersMutation(
  options: { onMutate?: () => void; onSuccess?: () => void } = {},
) {
  const client = useApiQueryClient()

  const currentUser = useCurrentUser()
  const orgSlug = useApiOrgSlug()
  const { invalidateApiQueries } = client

  const _bulkUpdateEncounters = useApiMutation(
    'patch',
    '/{org_slug}/encounters/bulk_update',
  )

  return useMutation<
    SuccessResponseJSON<operations['bulkUpdateEncounters']>,
    Error,
    BulkUpdateEncountersVariables
  >({
    mutationFn: async (variables) => {
      const { assigneeBillerUser, ...restVariables } = variables
      const promise = _bulkUpdateEncounters.mutateAsync({
        body: {
          ...restVariables,
          assignee_biller_user_id:
            assigneeBillerUser === null ? null : assigneeBillerUser?.id,
          enrichment_status_override: variables.enrichment_status_override,
          enrichment_status_override_reason:
            variables.enrichment_status_override_reason,
          flagged_for_joyful_followup: variables.flagged_for_joyful_followup,
          workflow_status: variables.workflow_status,
        },
        params: {
          path: {
            org_slug: orgSlug,
          },
        },
      })
      const toastId = crypto.randomUUID()
      toast.promise(promise, {
        id: toastId,
        loading: buildLoadingToastMessage(variables),
        success: (result) => {
          return {
            cancel:
              result.failed_count > 0 ? (
                <EncounterBulkUpdateResultToastDismiss toastId={toastId} />
              ) : undefined,
            duration: result.failed_count > 0 ? Infinity : undefined,
            icon: null,
            message: (
              <EncounterBulkUpdateResultToastContent
                currentUser={currentUser}
                result={result}
                variables={variables}
              />
            ),
          }
        },
      })
      return promise
    },

    onError: (error) => {
      toast.error(`Failed to update encounters: ${extractErrorMessage(error)}`)
    },

    onMutate: options?.onMutate,

    onSettled: () => {
      return invalidateApiQueries(
        '/{org_slug}/projects/{project_id}/encounters',
      )
    },

    onSuccess: async () => {
      await invalidateApiQueries(
        '/{org_slug}/encounters',
        '/{org_slug}/encounters/items',
        '/{org_slug}/encounters/{encounter_id}',
        '/{org_slug}/encounters/{encounter_id}/timeline',
        '/{org_slug}/projects/{project_id}/encounters',
        '/{org_slug}/requests/{practice_request_id}/encounters',
        '/{org_slug}/work_blocks',
      )

      options?.onSuccess?.()
    },
  })
}

function buildOptimisticEncounterUpdate({
  previousEncounter,
  variables,
}: {
  previousEncounter: components['schemas']['EncounterAPIResponse']
  variables: UpdateEncounterVariables
}) {
  const nextAssigneeBillerUser =
    variables.assigneeBillerUser === undefined
      ? previousEncounter.assignee_biller_user
      : variables.assigneeBillerUser
  const nextAssigneeBillerUserId =
    variables.assigneeBillerUser === undefined
      ? previousEncounter.assignee_biller_user_id
      : getAssigneeBillerUserId(variables.assigneeBillerUser)
  const nextFlaggedForJoyfulFollowupAt =
    variables.flagged_for_joyful_followup === undefined
      ? previousEncounter.flagged_for_joyful_followup_at
      : getFlaggedForJoyfulFollowupAt(variables.flagged_for_joyful_followup)
  const nextSnoozedUntilIsIndefinite =
    variables.snoozed_until_is_indefinite === undefined
      ? previousEncounter.snoozed_until_is_indefinite
      : getSnoozedUntilIsIndefinite(variables.snoozed_until_is_indefinite)
  const nextWorkflowStatus =
    variables.workflow_status === undefined
      ? getWorkflowStatusForFollowupFlag({
          flaggedForJoyfulFollowup: variables.flagged_for_joyful_followup,
          previousWorkflowStatus: previousEncounter.workflow_status,
        })
      : variables.workflow_status

  return {
    ...previousEncounter,
    assignee_biller_user: nextAssigneeBillerUser,
    assignee_biller_user_id: nextAssigneeBillerUserId,
    enrichment_status_override:
      variables.enrichment_status_override === undefined
        ? previousEncounter.enrichment_status_override
        : variables.enrichment_status_override,
    enrichment_status_override_reason:
      variables.enrichment_status_override_reason === undefined
        ? previousEncounter.enrichment_status_override_reason
        : variables.enrichment_status_override_reason,
    flagged_for_joyful_followup_at: nextFlaggedForJoyfulFollowupAt,
    snoozed_until:
      variables.snoozed_until === undefined
        ? previousEncounter.snoozed_until
        : variables.snoozed_until,
    snoozed_until_is_indefinite: nextSnoozedUntilIsIndefinite,
    workflow_status: nextWorkflowStatus,
  }
}

function getAssigneeBillerUserId(
  assigneeBillerUser: UpdateEncounterVariables['assigneeBillerUser'],
) {
  if (!assigneeBillerUser) {
    return null
  }

  return assigneeBillerUser.id
}

function getFlaggedForJoyfulFollowupAt(
  flaggedForJoyfulFollowup: boolean | null,
) {
  if (flaggedForJoyfulFollowup) {
    return new Date().toISOString()
  }

  return null
}

function getSnoozedUntilIsIndefinite(snoozedUntilIsIndefinite: boolean | null) {
  if (snoozedUntilIsIndefinite === null) {
    return false
  }

  return snoozedUntilIsIndefinite
}

function getWorkflowStatusForFollowupFlag({
  flaggedForJoyfulFollowup,
  previousWorkflowStatus,
}: {
  flaggedForJoyfulFollowup: boolean | null | undefined
  previousWorkflowStatus: components['schemas']['EncounterAPIResponse']['workflow_status']
}) {
  if (flaggedForJoyfulFollowup) {
    return 'NEW'
  }

  // oxlint-disable-next-line joyful/prefer-boolean-check
  if (flaggedForJoyfulFollowup === false) {
    return null
  }

  return previousWorkflowStatus
}
